// src/api/remittance.js
//
// CML remittance reconciliation — the run history, and starting a run.
//
// Uses the app's shared transport, NOT a bare axios client. That is not a style preference: in
// production the SWA Function proxy only serves `/api/recon/*` (api/recon/function.json binds
// `recon/{*path}`), staticwebapp.config.json authenticates only `/api/*`, and its
// navigationFallback rewrites anything else to index.html. A root-relative `/remittance-runs`
// would therefore return the SPA's HTML with a 200, and `Array.isArray(response.data)` would be
// false — an empty grid and no error, which reads as "no runs recorded yet".
//
// createAppTransport resolves to /api/recon in production and to pam.wsq.io in dev, exactly as the
// recon entries and crosswalk calls do.
//
// Route names are recon_server's, not a REST shape: kebab-case with query parameters, matching
// /recon-entries and /security-xref beside them. structure_data emits a BARE JSON array, so
// `response.data` is the row list with no envelope to unwrap.
import api from './index';

/** One row per run, newest first — the history grid reads this alone. */
export const fetchRemittanceRuns = (params = {}) => {
  const query = new URLSearchParams(
    Object.entries({ includeSummary: false, ...params })
      .filter(([, value]) => value !== undefined && value !== null),
  ).toString();
  return api.get(query ? `/remittance-runs?${query}` : '/remittance-runs');
};

/** One run's reconciliation — every loan in scope, breaks first then largest difference. */
export const fetchRemittanceRunLoans = (runId) =>
  api.get(`/remittance-run-loans?runId=${encodeURIComponent(runId)}`);


/** Runnable months, newest first. */
export const fetchRemittancePeriods = () => api.get('/remittance-periods');

/** Start an asynchronous reconciliation. A 202 means accepted, not finished. */
export const triggerRemittanceRun = (period, launchId) =>
  api.post('/remittance-run', { period, launch_id: launchId });

/** Worker state for failures that never produce a history row. */
export const fetchRemittanceRunState = (launchId) =>
  api.get(`/remittance-run-state?launchId=${encodeURIComponent(launchId)}`);

/** Current file versions for one month, plus anything nobody could read. */
export const fetchRemittanceFiles = (period) =>
  api.get(`/remittance-files?period=${encodeURIComponent(period)}`);

/**
 * Store one servicer file against *period*. A 202 means accepted, not necessarily stored -- read
 * `stored` and `reason` on the body.
 *
 * `source_id` is what makes two uploads the same logical file: the server derives its dedupe key
 * as `source_key(file_name)::sha256(source_id)`, so a value that varies per upload makes every
 * re-upload a NEW active file and a corrected one counts its cash twice. See sourceIdFor.
 *
 * No Content-Type header on purpose. Setting 'multipart/form-data' by hand looks required and is
 * a no-op here -- axios calls setContentType(undefined) for FormData in a browser so the browser
 * supplies the boundary (helpers/resolveConfig.js). Supplying it without a boundary is what breaks
 * multipart, so leave this alone.
 */
export const uploadRemittanceFile = (period, file, sourceId) => {
  const form = new FormData();
  form.append('period', period);
  form.append('source_id', sourceId);
  form.append('file', file, file.name);
  return api.post('/remittance-upload', form);
};

/**
 * Retire the current version of one stored file so future runs stop reading it.
 *
 * Gated on `recon.remittance:ingest` server-side, unlike every other remittance write: the intake
 * agent never deletes, so the gate locks out nobody it exists for. A 403 here is real.
 *
 * Append-only. The engine writes a `Deleted` version rather than removing rows, and a finished run
 * reconciled the input snapshot it captured at launch — so this changes future runs only, and
 * cannot rewrite a result somebody has already read.
 */
export const deleteRemittanceFile = (period, sourceKey, reason) =>
  api.post('/remittance-delete-file', { period, source_key: sourceKey, reason });
